import React, { useEffect, useState } from 'react';

import { useNESteps } from 'context/NEStepsContext';
import { useFormik } from 'formik';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { SectionContent } from 'components/DEBForm/SectionContent';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { PlusIcon } from 'components/Icons/PlusIcon';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { NEExpertTable } from 'components/NEComponents/NEExpertsTable/NEExpertsTable';
import { NENewExpertPopup } from 'components/NEComponents/NENewExpertPopup/NENewExpertPopup';
import { NEAgreementSection } from 'components/NEForm/NEAgreementSection';
import {
  NE_CURRENT_TEST_SUITE,
  shouldFillNETestData,
} from 'components/NEForm/NETestdata/NETestSetup';
import { NE_ERROR_MESSAGE } from 'components/NEForm/Utils/NEError';
import { YNEStepFormPageSchema } from 'components/NEValidation/YNEStepFormPageSchema';

import { NE_ACTIVE_STEP } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import { FFormDebug } from 'utils/FFormDebug';
import { checkKeyDown } from 'utils/FormUtils';
import { isRegisteringInstallerOnlyHilfsbetrieb } from 'utils/InstallerUtils';

import { NEFormIV } from './NEFormIV';
import { NE_FIRST_SECTION_INPUTS, checkErrors } from './NEFormUtility';

export const NEFormPage = ({ energyAgreementLink, gasAgreementLink, setButtonErrorMessage }) => {
  const { setSubmittedValuesWithStep, getSubmittedValuesWithStep } = useNESteps();
  const {
    comboBoxOptions,
    gridnetProviders,
    isLoading: isLoadingFormUtilityData,
  } = useFormUtilityData();

  const branchSelectionValues = getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION);

  const [newExpertPopupOpen, setNewExpertPopupOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const initialValues = shouldFillNETestData
    ? NE_CURRENT_TEST_SUITE.step2
    : NEFormIV(
        getSubmittedValuesWithStep(NE_ACTIVE_STEP.EMAIL_INPUT),
        getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION),
        getSubmittedValuesWithStep(NE_ACTIVE_STEP.FORM)
      );
  const formik = useFormik({
    validationSchema: YNEStepFormPageSchema(
      getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)
    ),
    initialValues,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
  });

  const { values, errors: formErrors } = formik;

  useEffect(() => {
    setSubmittedValuesWithStep(NE_ACTIVE_STEP.FORM, values);
  }, [values]);

  useEffect(() => {
    const realErrors = checkErrors(
      values,
      getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION),
      formErrors
    );
    setErrors(realErrors);
    if (realErrors.length > 0) {
      setButtonErrorMessage(NE_ERROR_MESSAGE);
    } else {
      setButtonErrorMessage('');
    }
  }, [values, formErrors]);
  if (isLoadingFormUtilityData) {
    return <LoadingIndicator />;
  }
  return (
    <form
      onKeyDown={checkKeyDown}
      onSubmit={(e) => {
        e.preventDefault();
      }}>
      <FormPageWrapper>
        {NE_FIRST_SECTION_INPUTS.map((section) => {
          const evaluateConditionOptions = {
            isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
            branchSelectionValues,
            values,
          };
          if (evaluateCondition(section.whenShouldSectionRender, evaluateConditionOptions)) {
            return (
              <FormSection
                key={section.id}
                title={section.title}
                subtitle={section.subtitle}
                infoText={section.infoText}
                tooltip={section.tooltip}
                navigationTooltip={section.navigationTooltip}>
                <SectionContent
                  section={section}
                  formik={formik}
                  values={values}
                  initialValues={initialValues}
                  comboBoxOptions={comboBoxOptions}
                  evaluateConditionOptions={evaluateConditionOptions}
                />
              </FormSection>
            );
          }
          return null;
        })}
        <FormSection title="Fachkräfte hinzufügen">
          <NEExpertTable
            formik={formik}
            errors={errors}
            branchSelectionValues={getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)}
          />
          <ActionButton
            inverted
            icon={<PlusIcon />}
            title={<div className="text-nowrap"> Fachkraft hinzufügen</div>}
            onClick={() => {
              setNewExpertPopupOpen(true);
            }}
          />
          <NENewExpertPopup
            branchSelectionValues={getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)}
            popupOpen={newExpertPopupOpen}
            setPopupOpen={setNewExpertPopupOpen}
            mainFormik={formik}
          />
        </FormSection>
        <FormSection>
          <NEAgreementSection
            formik={formik}
            branchSelectionValues={getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)}
            energyAgreementLink={energyAgreementLink}
            gasAgreementLink={gasAgreementLink}
          />
        </FormSection>
      </FormPageWrapper>
      <FFormDebug values={getSubmittedValuesWithStep(NE_ACTIVE_STEP.FORM)} />
    </form>
  );
};

NEFormPage.propTypes = {
  energyAgreementLink: PropTypes.string,
  gasAgreementLink: PropTypes.string,
  setButtonErrorMessage: PropTypes.func.isRequired,
};

NEFormPage.defaultProps = { energyAgreementLink: '', gasAgreementLink: '' };
